<template>
  <v-card>
    <v-card-title>{{
      isEditMode ? 'Edit Account' : 'Add New Account'
    }}</v-card-title>
    <v-card-text>
      <v-form ref="form">
        <v-text-field
          v-model="formData.account_name"
          label="Account Name"
          :readonly="formData.source !== 'INTERNAL'"
        />
        <v-text-field
          v-if="isEditMode"
          v-model="formData.account_number"
          label="Account Number"
          :readonly="formData.source === 'XERO'"
        />
        <v-text-field
          v-if="isEditMode"
          v-model="formData.routing_number"
          label="Routing Number"
        />
        <v-autocomplete
          v-model="formData.currency"
          :items="currencyOptions"
          :label="$t('Currency (ISO 4217)')"
          :hint="
            $t(
              'Leave blank to fall back to country / tenant default. Used by the bank reconciliation matcher for FX-aware matching.'
            )
          "
          persistent-hint
          outlined
          dense
          clearable
          class="mt-3"
        />
        <v-text-field
          v-if="isEditMode"
          v-model="formData.sepa_tax_id"
          :label="$t('SEPA Tax ID')"
        />
        <v-select
          v-if="isEditMode"
          v-model="formData.sepa_tax_scheme"
          :items="taxSchemeOptions"
          :label="$t('SEPA Tax Scheme')"
          outlined
          dense
        />
        <multi-property-picker
          v-if="isEditMode"
          :disabled="disableListingsPicker"
          class="input-auto"
          :value="formData.listings?.map(l => l.id)"
          :label="$t('Connected Listings')"
          :hide-btns="true"
          @input="onListingsChange"
        />
        <multi-property-picker
          v-if="isEditMode"
          :disabled="disableOwnerListingsPicker"
          class="input-auto"
          :value="formData.owner_listings?.map(l => l.id)"
          :label="$t('Connected Owner Listings')"
          :hide-btns="true"
          @input="onOwnerListingsChange"
        />
        <v-text-field
          v-if="isEditMode && formData.code"
          class="input-auto"
          :value="formData.code"
          dense
          flat
          hide-details
          :readonly="item.source === 'XERO'"
        />
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn color="primary" @click="submit">{{
        isEditMode ? 'Save' : 'Add'
      }}</v-btn>
      <v-btn @click="onClose">Cancel</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import MultiPropertyPicker from 'components/multi-property-picker'
import CommonFunctions from 'components/mixins/common_functions'
import FormattersMixin from 'components/mixins/formatters-mixin'
import PermissionsMixin from 'components/mixins/permissions-mixin'
import { mapActions } from 'vuex'

export default {
  name: 'BankAccountModal',
  components: { MultiPropertyPicker },
  mixins: [CommonFunctions, PermissionsMixin, FormattersMixin],
  props: {
    item: Object,
    isEditMode: {
      type: Boolean,
      default: false,
    },
    afterSaveFunc: Function,
  },
  data() {
    return {
      formData: {},
      newBankAccount: {
        account_number: '',
        routing_number: '',
        account_name: 'NEW BANK ACCOUNT',
        code: '',
        source: 'INTERNAL',
        sepa_tax_id: '',
        sepa_tax_scheme: '',
        currency: null,
      },
    }
  },
  computed: {
    disableListingsPicker() {
      return !this.isEmpty(this.formData.owner_listings?.map(l => l.id))
    },
    disableOwnerListingsPicker() {
      return !this.isEmpty(this.formData.listings?.map(l => l.id))
    },
    taxSchemeOptions() {
      return [
        { text: 'TXID - Tax Identification Number', value: 'TXID' },
        { text: 'VAT - Value Added Tax Number', value: 'VAT' },
        { text: 'NIF - Número de Identificación Fiscal (Spain)', value: 'NIF' },
        { text: 'CIF - Código de Identificación Fiscal (Spain)', value: 'CIF' },
        { text: 'NIDN - National Identity Number', value: 'NIDN' },
        { text: 'SOSE - Social Security Number', value: 'SOSE' },
        { text: 'CUST - Customer Number', value: 'CUST' },
        { text: 'XARF - Verification Code', value: 'XARF' },
      ]
    },
    // Mirrors BankAccount::COUNTRY_TO_CURRENCY.values on the Rails side so the
    // UI exposes the same currency set the matcher's country fallback knows
    // about. Codes are sorted alphabetically for predictable scanning.
    currencyOptions() {
      return [
        'AED',
        'ARS',
        'AUD',
        'BRL',
        'CAD',
        'CHF',
        'CLP',
        'COP',
        'EUR',
        'GBP',
        'HKD',
        'ILS',
        'INR',
        'JPY',
        'MXN',
        'NZD',
        'SGD',
        'USD',
        'ZAR',
      ]
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.formData = {
        ...this.item,
        sepa_tax_id: this.item.sepa_tax_id || '',
        sepa_tax_scheme: this.item.sepa_tax_scheme || '',
        currency: this.item.currency || null,
      }
    } else {
      this.formData = { ...this.newBankAccount }
    }
  },
  methods: {
    ...mapActions('bankAccounts', [
      'updateBankAccount',
      'createNewBankAccount',
    ]),
    submit() {
      if (this.$refs.form.validate()) {
        const action = this.isEditMode ? this.updateAccount : this.saveAccount
        // Ensure SEPA tax fields are included in the payload.
        // Currency is sent as empty string when blank so the backend can
        // clear an explicit value and fall back to the country / tenant
        // default in BankAccount#currency.
        const payload = {
          ...this.formData,
          sepa_tax_id: this.formData.sepa_tax_id || '',
          sepa_tax_scheme: this.formData.sepa_tax_scheme || '',
          currency: this.formData.currency || '',
        }
        action(payload).then(() => {
          if (typeof this.afterSaveFunc === 'function') {
            this.afterSaveFunc(payload)
          }
          this.onClose()
        })
      }
    },
    saveAccount(accountData) {
      return this.createNewBankAccount(accountData)
    },
    updateAccount(accountData) {
      return this.updateBankAccount(accountData)
    },
    onListingsChange(listingIds) {
      this.formData.listings = listingIds.map(id => ({ id }))
      this.formData.listing_ids = listingIds
    },
    onOwnerListingsChange(ownerListingIds) {
      this.formData.owner_listings = ownerListingIds.map(id => ({ id }))
      this.formData.owner_listing_ids = ownerListingIds
    },
    onClose() {
      this.$store.commit('hideModal')
    },
  },
}
</script>

<style scoped></style>
